import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_URL, APP_URL } from '../../constants';

const CreateEditRoleForm = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();  // Using useNavigate for navigation

    // Determine whether we're creating or editing a role based on the pathname
    const pageType = {
        isNew: pathname.includes('new'),
        isEdit: pathname.includes('edit'),
    };

    const [name, setName] = useState('');
    const [permissions, setPermissions] = useState({
        user_edit: false,
        create_new_user: false,
        user_delete: false,
        view_user: false,
        create_order: false,
    });
    const [error, setError] = useState(null);
    const [roleId, setRoleId] = useState(null);

    // Get role data for editing
    useEffect(() => {
        if (pageType.isEdit) {
            const roleId = pathname.split('/').pop(); // assuming the id is at the end of the path
            setRoleId(roleId);

            // Fetch the role data from API
            axios.get(`${API_URL}/roles/${roleId}`)
                .then(response => {
                    const { name, permissions } = response.data;
                    setName(name);

                    // Map the permissions to the state correctly
                    setPermissions(prevPermissions => {
                        const updatedPermissions = { ...prevPermissions };
                        permissions.forEach(perm => {
                            if (updatedPermissions.hasOwnProperty(perm)) {
                                updatedPermissions[perm] = true; // Mark permission as true if it exists in the API response
                            }
                        });
                        return updatedPermissions;
                    });
                })
                .catch(error => {
                    toast.error('Error fetching role data');
                    console.error(error);
                });
        }
    }, [pageType.isEdit, pathname]);

    // Handle checkbox change
    const handleCheckboxChange = (event) => {
        const { name, checked } = event.target;
        setPermissions((prevPermissions) => ({
            ...prevPermissions,
            [name]: checked,
        }));
    };

    // Handle input change for name
    const handleNameChange = (event) => {
        setName(event.target.value);
    };

    // Submit the form
    const handleSubmit = async (event) => {
        event.preventDefault();

        const selectedPermissions = Object.keys(permissions).filter(
            (permission) => permissions[permission]
        );

        const roleData = {
            name: name,
            permissions: selectedPermissions,
        };

        try {
            if (pageType.isNew) {
                // Creating a new role
                const response = await axios.post(`${API_URL}/roles`, roleData, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                toast.success('Role created successfully');
                navigate(`/admin/user-role/edit/` + response.data.id); // 
            } else if (pageType.isEdit) {
                const response = await axios.patch(`${API_URL}/roles/${roleId}`, roleData, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                toast.success('Role updated successfully');
                navigate(`/admin/user-role/edit/` + roleId);
            }
        } catch (error) {
            setError('Error creating/updating role, please try again.');
            toast.error(error.response?.data?.message || 'Error, please try again.');
            console.error('Error:', error);
        }
    };

    return (
        <div>
            {error && <div style={{ color: 'red' }}>{error}</div>}
            <form onSubmit={handleSubmit} className='container'>
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
                    <h2>{pageType.isNew ? 'Create New Role' : 'Edit Role'}</h2>
                    <div className="btn-toolbar mb-2 mb-md-0">
                        <button type="button" className="btn btn-sm btn-outline-secondary rounded-0"><i className="bi bi-arrow-left me-2"></i>Back to List</button>
                        <button className="btn btn-primary ms-2 btn-dark rounded-0" type="submit">{pageType.isNew ? 'Create Role' : 'Update Role'}</button>
                    </div>
                </div>
                <div className='border p-4 pb-5 bg-white'>
                    {/* Role Name Input */}
                    <div className='mb-2 role-name'>
                        <label htmlFor="role-name" className='form-label'>Role Name:</label>
                        <input
                            type="text"
                            id="role-name"
                            value={name}
                            className="form-control form-control-sm"
                            onChange={handleNameChange}
                            required
                        />
                    </div>

                    {/* Permissions Checkboxes */}
                    <div>
                        <h5>Permissions:</h5>
                        {Object.keys(permissions).map((permission) => (
                            <div className="form-check" key={permission}>
                                <input
                                    type="checkbox"
                                    id={permission}
                                    name={permission}
                                    className='form-check-input'
                                    checked={permissions[permission]}
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor={permission} className='form-check-label'>{permission}</label>
                            </div>
                        ))}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CreateEditRoleForm;
